import moment, { Moment } from "moment";
import { DiscountedPrice, MomentComparisonResult } from "./types/venues.types";
import {
  SelectedSlot,
  TimeslotRepresentationsType,
  VenueConfigType,
  VenueOffer,
} from "@/services/types/venue.types";

export const sizeNamesForSport = (sport: string) => {
  switch (sport) {
    case "football":
      return "PITCH SIZE";
    case "basketball":
      return "COURT SIZE";
    case "cricket":
      return "FORMAT";
    case "badminton":
    case "pickleball":
    case "tabletennis":
      return "COURT TYPE";
    case "swimming":
      return "SIZE";
    default:
      return "COURT TYPE";
  }
};

export const setKeyForSport = (pitch: string) => {
  switch (pitch) {
    case "2s":
      return "2 v 2";
    case "3s":
      return "3 v 3";
    case "4s":
      return "4 v 4";
    case "5s":
      return "5 v 5";
    case "6s":
      return "6 v 6";
    case "7s":
      return "7 v 7";
    case "8s":
      return "8 v 8";
    case "9s":
      return "9 v 9";
    case "10s":
      return "10 v 10";
    case "11s":
      return "11 v 11";
    case "net":
      return "Nets";
    case "ac":
      return "A/C";
    case "nonac":
      return "Non A/C";
    case "hc":
      return "Half Court";
    case "fc":
      return "Full Court";
    case "semiOlympic":
      return "Semi-Olympic";
    case "mini":
      return "Mini";
    case "olympic":
      return "Olympic";
    case "ground":
    case "pitch":
      return "Box-Cricket";
    default:
      return pitch;
  }
};
export function getCourtLabel(sportName) {
  if (["basketball"].includes(sportName)) return "";
  switch (sportName) {
    case "football":
    case "ground":
      return "Pitch";
    case "net":
      return "Net";
    case "swimming":
      return "Spot";
    case "tabletennis":
      return "Table";
    default:
      return "Court";
  }
}

export const courtSizeNamesForSport = (sport: string, type: string) => {
  switch (true) {
    case sport == "football":
      return "No. of pitches";
    case sport == "basketball":
      return "No. of Courts";
    case sport == "cricket" && type == "net":
      return "No. of Nets";
    case sport == "cricket" && type !== "net":
      return "No. of pitches";
    case sport == "badminton":
    case sport == "pickleball":
    case sport == "tabletennis":
      return "No. of Courts";
    case sport == "swimming":
      return "No. of Spots";
    default:
      return "No. of Courts";
  }
};
export const getDatesFromBookingWindow = (total_days?: number): Moment[] => {
  const days: Moment[] = [];
  const no_of_days = total_days ?? 15;

  for (let i = 0; i <= no_of_days; i++) {
    const day = i === 0 ? moment() : moment().add(i, "day");
    days.push(day);
  }

  return days;
};

export function compareMoments(
  date1: Moment,
  date2: Moment,
  granularity: moment.unitOfTime.StartOf = "day"
): MomentComparisonResult {
  if (!moment.isMoment(date1) || !moment.isMoment(date2)) {
    return {
      isSame: false,
      isBefore: false,
      isAfter: false,
      error: "Invalid Moment object(s) provided.",
    };
  }

  return {
    isSame: date1.isSame(date2, granularity),
    isBefore: date1.isBefore(date2, granularity),
    isAfter: date1.isAfter(date2, granularity),
  };
}

interface SlotItem {
  timeRepresentation: string;
  available?: boolean;
  side?: any;
  timeOut?: boolean;
  show_time?: boolean;
  current_time?: boolean;
  toggle?: boolean;
  blocked?: boolean;
  sId?: number;
}

interface Slot {
  item: SlotItem[];
}

interface SlotResponse {
  slots?: Slot[];
}

interface AvailabilityResponse {
  slots_available?: Record<string, Record<string, number>>;
}

export const computeAvailability = (
  slotResponse: SlotResponse,
  availabilityResponse: AvailabilityResponse,
  courtSelected: string,
  venueDate: moment.Moment,
  courts: number
): Promise<Slot[]> => {
  return new Promise((resolve) => {
    const slots = slotResponse.slots || [];
    const slotsAvailability = availabilityResponse.slots_available || {};

    if (Object.keys(slotsAvailability).length > 0) {
      for (let i = 0; i < slots.length; i++) {
        const item = slots[i].item;

        for (let j = 0; j < item.length; j++) {
          const slotItem = item[j];

          Object.entries(slotsAvailability).forEach(([timeKey, courtMap]) => {
            if (timeKey === slotItem.timeRepresentation) {
              const value = courtMap;

              Object.entries(courtMap).forEach(([courtKey, countStr]) => {
                const currentTime = moment(moment().format("HH:mm"), "HH:mm");
                const startTime = moment(
                  slotItem.timeRepresentation.substring(0, 4),
                  "HHmm"
                );
                const endTime = moment(
                  slotItem.timeRepresentation.substring(5, 9),
                  "HHmm"
                );
                const isTimeNow = currentTime.isBetween(
                  startTime,
                  endTime,
                  undefined,
                  "[)"
                );

                const count = parseInt(countStr as any, 10);
                const dateToday = moment().format("YYYYMMDD");
                const venueDateStr = venueDate.format("YYYYMMDD");
                const slotStart = moment(
                  slotItem.timeRepresentation.substring(0, 4),
                  "HHmm"
                ).format("YYYYMMDDHHmm");
                const now = moment().format("YYYYMMDDHHmm");

                const isToday = venueDateStr === dateToday;
                const isFuture = venueDateStr > dateToday;
                const isPast = venueDateStr < dateToday;

                if (courtSelected === courtKey) {
                  if (
                    count <= 0 &&
                    count >= courts &&
                    isToday &&
                    slotStart < now
                  ) {
                    slotItem.available = false;
                    slotItem.side = value;
                    slotItem.timeOut = true;
                    slotItem.show_time = isToday;
                    slotItem.current_time = isTimeNow;
                    slotItem.toggle = false;
                  } else if (
                    count <= 0 &&
                    count >= courts &&
                    isToday &&
                    slotStart > now
                  ) {
                    slotItem.available = false;
                    slotItem.side = value;
                    slotItem.timeOut = false;
                    slotItem.toggle = false;
                    slotItem.show_time = isToday;
                    slotItem.current_time = isTimeNow;
                  } else if (count <= 0 && count >= courts && isFuture) {
                    slotItem.available = false;
                    slotItem.side = value;
                    slotItem.timeOut = false;
                    slotItem.toggle = false;
                    slotItem.show_time = isToday;
                    slotItem.current_time = isTimeNow;
                  } else if (count <= 0 && count >= courts && isPast) {
                    slotItem.available = false;
                    slotItem.side = value;
                    slotItem.timeOut = true;
                    slotItem.toggle = false;
                    slotItem.show_time = isToday;
                    slotItem.current_time = isTimeNow;
                  } else if (count <= 0 && count >= courts && !isToday) {
                    slotItem.available = false;
                    slotItem.side = value;
                    slotItem.timeOut = false;
                    slotItem.toggle = false;
                    slotItem.show_time = isToday;
                    slotItem.current_time = isTimeNow;
                  } else if (count >= 0 && isPast) {
                    if (count >= courts) {
                      slotItem.available = true;
                      slotItem.side = value;
                      slotItem.timeOut = true;
                      slotItem.toggle = false;
                      slotItem.show_time = isToday;
                      slotItem.current_time = isTimeNow;
                    } else {
                      slotItem.available = false;
                      slotItem.side = value;
                      slotItem.timeOut = true;
                      slotItem.toggle = false;
                      slotItem.show_time = isToday;
                      slotItem.current_time = isTimeNow;
                    }
                  } else {
                    const midnight = moment(2400, "HHmm").format(
                      "YYYYMMDDHHmm"
                    );
                    const isBeforeNow =
                      slotItem.sId === 48 ? midnight < now : slotStart < now;

                    if (isToday && isBeforeNow) {
                      if (count >= courts) {
                        slotItem.available = true;
                        slotItem.side = value;
                        slotItem.timeOut = true;
                        slotItem.show_time = isToday;
                        slotItem.current_time = isTimeNow;
                        slotItem.toggle = false;
                      } else {
                        slotItem.available = false;
                        slotItem.side = value;
                        slotItem.timeOut = true;
                        slotItem.show_time = isToday;
                        slotItem.current_time = isTimeNow;
                        slotItem.toggle = false;
                      }
                    } else {
                      if (count >= courts) {
                        slotItem.available = true;
                        slotItem.timeOut = false;
                        slotItem.blocked = false;
                        slotItem.toggle = false;
                        slotItem.show_time = isToday;
                        slotItem.current_time = isTimeNow;
                      } else {
                        slotItem.available = false;
                        slotItem.timeOut = false;
                        slotItem.blocked = false;
                        slotItem.toggle = false;
                        slotItem.show_time = isToday;
                        slotItem.current_time = isTimeNow;
                      }
                    }
                  }
                }
              });
            }
          });
        }
      }
    }

    resolve(slots);
  });
};

export const showOffersForToday = (date1, offer: VenueOffer[]) => {
  let date = date1 ? date1 : moment();
  let offersForToday: VenueOffer[] = [];
  for (let i = 0; i < offer.length; i++) {
    if (offer[i].offer_type === "customDay") {
      //// also to do for ios build  and issue we were checking here only day not with dates
      let start_date = moment(offer[i].start_date).format("YYYY-MM-DD");
      let end_date = moment(offer[i].end_date).format("YYYY-MM-DD");
      let current_date =
        date.format("YYYY-MM-DD") === moment().format("YYYY-MM-DD")
          ? moment()
          : date;
      offer[i].days.map((day) => {
        if (
          moment(moment(current_date).format("YYYY-MM-DD")).isBetween(
            start_date,
            end_date,
            null,
            "[]"
          ) &&
          date.format("dddd").toLowerCase() === day.day &&
          day.isFixed
        ) {
          offersForToday.push(offer[i]);
        }
      });
    } else {
      let start_date = moment(offer[i].start_date).format("YYYY-MM-DD");
      let end_date = moment(offer[i].end_date).format("YYYY-MM-DD");
      let current_date =
        date.format("YYYY-MM-DD") === moment().format("YYYY-MM-DD")
          ? moment()
          : date;
      if (
        offer[i].status === true &&
        moment(moment(current_date).format("YYYY-MM-DD")).isBetween(
          start_date,
          end_date,
          null,
          "[]"
        )
      ) {
        offersForToday.push(offer[i]);
      }
    }
  }
  return offersForToday;
};

export const isTimeBetween = (
  serverTime: string,
  startTime: string,
  endTime: string
): boolean => {
  const format = "HH:mm";
  const server = moment(serverTime, format);
  const start = moment(startTime, format);
  const end = moment(endTime, format);

  if (!server.isValid() || !start.isValid() || !end.isValid()) {
    throw new Error("Invalid time format. Use HH:mm.");
  }

  if (end.isSameOrBefore(start)) {
    // Time range crosses midnight
    return server.isAfter(start) || server.isBefore(end);
  }

  return server.isSameOrAfter(start) && server.isBefore(end);
};

export const applyOffer = (
  offers: VenueOffer[],
  slots: TimeslotRepresentationsType[],
  date: string,
  numberOfCourts: number
): [number, boolean, string | number | null] => {
  let offerIndex: string | number = 0;
  let discountedPrices: DiscountedPrice[] = [];
  let appliedSlots: DiscountedPrice[] = [];

  if (slots.length > 1) {
    slots.forEach((slot, index) => {
      const basePrice: DiscountedPrice = {
        [slot.timeRepresentation]: slot.price,
        amount: slot.price,
        type: "true",
      };
      discountedPrices.push(basePrice);

      const time = slot.timeRepresentation.slice(0, 4);

      offers.forEach((offer) => {
        const meetsHourRequirement =
          Math.floor(slots.length / 2) >= offer.minhours;
        const withinTime = isTimeBetween(
          time,
          offer.start_time,
          offer.end_time
        );
        const respectsMinHourExclusive =
          !offer.min_hour_exculsive ||
          multiplyMinhours(index, offer.minhours, slots.length);

        if (meetsHourRequirement && withinTime && respectsMinHourExclusive) {
          let discountedAmount = 0;

          if (offer.discount_type === "discount") {
            discountedAmount = slot.price - slot.price * (offer.discount / 100);
          } else if (offer.discount_type === "discountAmount") {
            const unitDiscount =
              Math.round((offer.discount / (offer.minhours * 2)) * 10) / 10;
            discountedAmount = Math.max(slot.price - unitDiscount, 0);
          }

          discountedPrices.push({
            amount: discountedAmount,
            [slot.timeRepresentation]: discountedAmount,
            selectedOfferId: offer._id,
            actual_price: slot.price,
            discount: offer.discount,
          });

          offerIndex = offer._id;
        }
      });

      // Get the lowest amount for that slot
      const key = slot.timeRepresentation;
      const minPrice = discountedPrices.reduce<DiscountedPrice | null>(
        (min, current) => {
          const currentValue = current?.[key];
          const minValue = min?.[key];

          if (
            typeof currentValue === "number" &&
            (min === null ||
              (typeof minValue === "number" && currentValue < minValue))
          ) {
            return current;
          }
          return min;
        },
        null
      );

      if (minPrice) {
        appliedSlots.push(minPrice);
      }
    });
  }

  const totalSum = appliedSlots.reduce(
    (sum, item) => sum + (item.amount || 0),
    0
  );
  return [Math.round(totalSum * numberOfCourts), true, offerIndex];
};
export const multiplyMinhours = (index, minhours, total_slots) => {
  let muliple_min_hour = minhours * minhours;
  let below_the_index = Math.floor(total_slots / muliple_min_hour);
  let max_index = muliple_min_hour * below_the_index;
  if (index + 1 <= max_index) {
    return true;
  } else {
    return false;
  }
};
export const checkForContinuity = (slots) => {
  let x = true;
  for (let i = 0; i < slots.length - 1; i++) {
    let g = slots[i + 1].sId - slots[i].sId;
    if (!(Math.abs(g) <= 1)) {
      x = false;
    }
  }
  return x;
};
const getLatestSelection = (slots, x) => {
  for (let i = 0; i < slots.length; i++) {
    slots[i].toggle = false;
  }
  x.toggle = true;
  slots = Object.assign([], [x]);
  return [x];
};
const getTimeLatestSelection = (slots, x) => {
  slots.push(x);
  return slots.splice(0, slots.length - 1);
};

const getCost = (slots, number_of_courts): [number, boolean, null] => {
  let x = 0;
  for (let i = 0; i < slots.length; i++) {
    x += slots[i].price * number_of_courts;
  }
  return [x, true, null]; // now it's a 3-element tuple
};

export function removeAttr<T extends Record<string, any>>(
  arr: T[],
  attr: keyof T,
  value: any
): T[] {
  let i = arr.length;
  while (i--) {
    if (
      arr[i] &&
      Object.prototype.hasOwnProperty.call(arr[i], attr) &&
      arr[i][attr] === value
    ) {
      arr.splice(i, 1);
    }
  }
  return arr;
}

export const prepareBookingsObject = (
  slotData,
  name,
  state,
  venue_offers,
  applyOffer,
  allSlots
) => {
  const latestSlotData: SelectedSlot = slotData as SelectedSlot;
  if (
    state.selected_slots?.length &&
    state.selected_slots.find((data) => latestSlotData.sId === data.sId)
  ) {
    //dont toggle if already selected
    latestSlotData.toggle = false;
  } else if (latestSlotData?.available && !latestSlotData?.timeOut) {
    latestSlotData.toggle = !latestSlotData?.toggle;
  }

  let collection = state?.selected_slots?.length
    ? [...state.selected_slots]
    : [];

  let slotList = allSlots;
  const currentDate =
    state.booking_date.format("YYYY-MM-DD") === moment().format("YYYY-MM-DD")
      ? moment()
      : state.booking_date;

  // Add or remove latestSlotData
  latestSlotData.toggle
    ? collection.push(latestSlotData)
    : removeAttr(collection, "sId", latestSlotData.sId);

  collection.sort(function (a, b) {
    return a.sId - b.sId;
  });
  if (collection.length > 1 && !checkForContinuity(collection)) {
    collection = [...getLatestSelection(collection, latestSlotData)];
    const b = [
      ...getTimeLatestSelection(state.timeslotRepresentations, latestSlotData),
    ];
  }
  collection = collection
    ?.filter(
      (coll, index, self) => index === self.findIndex((u) => u.sId === coll.sId)
    )
    .sort((a, b) => a.sId - b.sId);
  const timeslot = collection.length
    ? `${collection[0].time} - ${collection[collection.length - 1].timeEnd} ${collection[collection.length - 1].representation}`
    : "";
  let discountAmount: [number, boolean, string | null];

  if (venue_offers?.length > 0 && collection.length > 1) {
    const offers = venue_offers.filter((offer) => {
      const startDate = moment(offer.start_date).format("YYYY-MM-DD");
      const endDate = moment(offer.end_date).format("YYYY-MM-DD");

      const startTime = collection[0].timeRepresentation.slice(0, 4);
      const endTime = collection[
        collection.length - 1
      ].timeRepresentation.slice(0, 4);

      const timeCheck = offer.min_hour_exculsive
        ? isTimeBetween(startTime, offer.start_time, offer.end_time) &&
          isTimeBetween(endTime, offer.start_time, offer.end_time)
        : true;

      const day = currentDate.format("dddd").toLowerCase();
      const dateValid = moment(currentDate.format("YYYY-MM-DD")).isBetween(
        startDate,
        endDate,
        undefined,
        "[]"
      );

      if (offer.offer_type === "customDay" && timeCheck) {
        return (
          (offer.set_format_status ? offer.format === state.category : true) &&
          offer.days?.some((d) => d.isFixed && d.day === day) &&
          dateValid
        );
      }

      if (offer.offer_type === "allDay" && timeCheck) {
        return (
          (offer.set_format_status ? offer.format === state.category : true) &&
          dateValid
        );
      }

      return false;
    });

    discountAmount = applyOffer(
      offers.slice(0, 6),
      collection,
      currentDate,
      state.courts
    );
  } else {
    discountAmount = getCost(collection, state.courts);
  }

  const price = getCost(collection, state.courts)[0];
  const discountedPrice = discountAmount[0];

  let config = {
    valid: true,
    slots: slotList,
    validity: collection.length > 0,
    timeslotRepresentations: collection,
    selected_slots: collection,
    slot_time: timeslot,
    timeSelected: timeslot,
    price: price,
    offer: discountAmount[1],
    discountedPrice: discountedPrice,
    switch_toggle: !state.switch_toggle,
    selectedOfferId: discountAmount[2] ?? 0,
  };
  return config;
};
export const isSlotActive = (slot) =>
  !slot.timeOut && !slot.blocked && slot.available;

export const getTTCurrentTimezone = () => {
  const TIMELINE_DATA = [
    { label: "Twilight", value: 0 },
    { label: "Morning", value: 1 },
    { label: "Noon", value: 2 },
    { label: "Evening", value: 3 },
  ];
  let current_time = parseInt(moment().format("Hmm"), 10);
  if (current_time < 540 && current_time >= 0) {
    return TIMELINE_DATA[0];
  } else if (current_time > 540 && current_time < 1140) {
    return TIMELINE_DATA[1];
  } else if (current_time > 1140 && current_time < 1740) {
    return TIMELINE_DATA[2];
  } else if (current_time > 1740) {
    return TIMELINE_DATA[3];
  }
  return TIMELINE_DATA[0];
};
export const setbaseFormat = (type) => {
  if (type == "4s") {
    return "4 v 4";
  } else if (type == "5s") {
    return "5 v 5";
  } else if (type == "6s") {
    return "6 v 6";
  } else if (type == "7s") {
    return "7 v 7";
  } else if (type == "8s") {
    return "8 v 8";
  } else if (type == "9s") {
    return "9 v 9";
  } else if (type == "10s") {
    return "10 v 10";
  } else if (type == "11s") {
    return "11 v 11";
  } else if (type == "12s") {
    return "12 v 12";
  } else if (type == "net") {
    return "nets";
  } else if (type == "ground") {
    return "5 v 5";
  } else if (type == "nonac") {
    return "doubles";
  } else if (type == "ac") {
    return "doubles";
  } else if (type == "hc") {
    return "3 v 3";
  } else if (type == "fc") {
    return "5 v 5";
  } else if (type == "semiOlympic") {
    return "Semi-Olympic";
  } else if (type == "olympic") {
    return "Olympic";
  } else {
    return "";
  }
};

export const setNoOfPlayers = (type) => {
  if (type == "4s") {
    return 8;
  } else if (type == "5s") {
    return 10;
  } else if (type == "6s") {
    return 12;
  } else if (type == "7s") {
    return 14;
  } else if (type == "8s") {
    return 16;
  } else if (type == "9s") {
    return 18;
  } else if (type == "10s") {
    return 20;
  } else if (type == "11s") {
    return 22;
  } else if (type == "12s") {
    return 24;
  } else if (type == "net") {
    return 2;
  } else if (type == "ground") {
    return 10;
  } else if (type == "nonac") {
    return 4;
  } else if (type == "ac") {
    return 4;
  } else if (type == "hc") {
    return 3;
  } else if (type == "fc") {
    return 10;
  } else if (type == "singles") {
    return 2;
  } else if (type == "doubles") {
    return 4;
  } else return 4;
};

export const getBookingAmountPrice = (
  amount,
  payment_limit,
  service_fee = 0
) => {
  return amount * ((payment_limit ? payment_limit : 10) / 100) + service_fee;
};
